import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMaterialUIController, setMiniSidenav } from "context";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import Divider from "@mui/material/Divider";
import broilerIcon from "assets/images/icons/omnio/broiler.png";
import { Link } from "react-router-dom";
import {
  broilerDetailsUrl,
  broilerListUrl,
  environmentalAlertsTriggerUrl,
  kpiAlertsTriggerUrl,
  activityAlertsTriggerUrl,
} from "api/endpoints";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const BROILER_CACHE_KEY = "OmnioBroilerLandingCache";
const FACILITY_SELECTION_KEY = "facilityLandingSelections";
const readBroilerCache = () => {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(BROILER_CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { data: parsed, timestamp: null };
    }
    if (parsed && Array.isArray(parsed.data)) {
      return { data: parsed.data, timestamp: parsed.timestamp ?? null };
    }
  } catch (error) {
    console.warn("Unable to read broiler cache", error);
  }

  return null;
};

const writeBroilerCache = (broilerData) => {
  if (
    typeof window === "undefined" ||
    typeof window.localStorage === "undefined" ||
    !Array.isArray(broilerData)
  ) {
    return null;
  }

  const cachePayload = {
    data: broilerData,
    timestamp: Date.now(),
  };

  try {
    window.localStorage.setItem(BROILER_CACHE_KEY, JSON.stringify(cachePayload));
  } catch (error) {
    console.warn("Unable to write broiler cache", error);
  }

  return cachePayload;
};

function BroilerLanding() {
  const cacheRef = useRef(readBroilerCache());
  const fetchInFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  const [broilers, setBroilers] = useState(() => cacheRef.current?.data || []);
  const [loading, setLoading] = useState(() => !cacheRef.current?.data?.length);
  const [refreshing, setRefreshing] = useState(false);
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false
  );
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const triggerFacilityId = useMemo(() => {
    if (typeof window === "undefined") return "";

    const normalizeId = (value) => {
      if (value == null) return "";
      const str = `${value}`.trim();
      if (!str) return "";
      const lower = str.toLowerCase();
      if (lower === "null" || lower === "undefined") return "";
      return str;
    };

    try {
      const storedRaw = window.localStorage.getItem(FACILITY_SELECTION_KEY);
      if (storedRaw) {
        const stored = JSON.parse(storedRaw);
        const storedId = normalizeId(
          stored?.facilityId ?? stored?.facilityID ?? stored?.facility ?? stored?.id ?? stored?.ID
        );
        if (storedId) return storedId;
      }
    } catch (error) {
      console.warn("Unable to read stored facility selection", error);
    }

    const firstWithId = Array.isArray(broilers)
      ? broilers.find((entry) => entry && entry.id != null)
      : null;

    return normalizeId(firstWithId?.id);
  }, [broilers]);

  useEffect(() => {
    // Collapse sidenav by default on this page (all sizes)
    try {
      setMiniSidenav(dispatch, true);
    } catch {}
  }, [dispatch]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!triggerFacilityId || isOffline || typeof window === "undefined") return undefined;

    let cancelled = false;
    const requestConfig = {
      headers: { "Content-Type": "application/json" },
      omnioOffline: { skipAuthHeader: true },
      withCredentials: false,
    };

    const triggerEndpoint = async (kind) => {
      if (cancelled) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const urlMap = {
        environmental: environmentalAlertsTriggerUrl(triggerFacilityId),
        kpi: kpiAlertsTriggerUrl(triggerFacilityId),
        activity: activityAlertsTriggerUrl(triggerFacilityId),
      };
      const url = urlMap[kind];
      if (!url) return;
      try {
        await axios.post(url, null, requestConfig);
      } catch (error) {
        if (!cancelled) {
          console.error(`Failed to trigger ${kind} alerts`, error);
        }
      }
    };

    const triggerEnvironmentalImmediately = () => {
      void triggerEndpoint("environmental");
    };

    triggerEnvironmentalImmediately();

    const envIntervalId = setInterval(() => {
      if (!cancelled) {
        void triggerEndpoint("environmental");
      }
    }, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(envIntervalId);
    };
  }, [isOffline, triggerFacilityId]);

  const fetchBroilerData = useCallback(async () => {
    if (fetchInFlightRef.current) return;

    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    const isBrowserEnv = typeof window !== "undefined";

    if (offline) {
      if (isBrowserEnv) {
        cacheRef.current = readBroilerCache();
      }
      if (isMountedRef.current) {
        setIsOffline(true);
        const cached = cacheRef.current?.data || [];
        setBroilers(cached);
        if (!cached.length) {
          setLoading(false);
        }
        setRefreshing(false);
      }
      return;
    }

    fetchInFlightRef.current = true;

    try {
      const hasCachedData = !!cacheRef.current?.data?.length;
      if (isMountedRef.current) {
        setIsOffline(false);
        if (!hasCachedData) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }
      }

      const sessionRaw =
        isBrowserEnv && typeof window.localStorage !== "undefined"
          ? window.localStorage.getItem("OmnioLogin")
          : null;
      const session = sessionRaw ? JSON.parse(sessionRaw) : null;

      if (!session?.ID) {
        console.warn("No valid session ID found.");
        if (isMountedRef.current) {
          setLoading(false);
        }
        return;
      }

      const userID = session.ID;

      const response = await axios.post(
        broilerListUrl(userID),
        {},
        {
          headers: { "Content-Type": "application/json" },
          omnioOffline: { skipAuthHeader: true },
        }
      );

      const broilerList = Array.isArray(response.data) ? response.data : [];
      if (isBrowserEnv && typeof window.localStorage !== "undefined") {
        window.localStorage.setItem("broilers", JSON.stringify(broilerList));
      }
      console.log("Fetched broilerIDs:", broilerList);

      const broilerDetailRefs = broilerList
        .map((entry) => ({
          facilityId: entry?.broilerID ?? entry?.id ?? null,
          cycleId: entry?.cycleId ?? entry?.cycleID ?? entry?.CycleId ?? entry?.CycleID ?? null,
        }))
        .filter(({ facilityId }) => facilityId !== null && facilityId !== undefined);

      const detailResponses = await Promise.all(
        broilerDetailRefs.map(async ({ facilityId, cycleId }) => {
          try {
            const detailRes = await axios.post(
              broilerDetailsUrl(facilityId, cycleId),
              {},
              {
                headers: {
                  "Content-Type": "application/json",
                },
                omnioOffline: { skipAuthHeader: true },
              }
            );
            return Array.isArray(detailRes.data) ? detailRes.data : [];
          } catch (error) {
            console.error(`Failed to fetch details for broiler ${facilityId}`, error);
            return [];
          }
        })
      );

      const broilerDetails = detailResponses.flat();

      if (!isMountedRef.current) return;

      setBroilers(broilerDetails);
      cacheRef.current = writeBroilerCache(broilerDetails) || {
        data: broilerDetails,
        timestamp: Date.now(),
      };
      setLoading(false);
    } catch (error) {
      console.error("Error fetching broiler IDs:", error);
      const networkIssue =
        !error?.response &&
        (error?.code === "ERR_NETWORK" ||
          (typeof error?.message === "string" && error.message.toLowerCase().includes("network")));

      if (isBrowserEnv) {
        cacheRef.current = readBroilerCache();
      }

      if (isMountedRef.current) {
        if (networkIssue) {
          setIsOffline(true);
        }
        setBroilers(cacheRef.current?.data || []);
        setLoading(false);
      }
    } finally {
      fetchInFlightRef.current = false;
      if (isMountedRef.current) {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    let refreshTimer = null;

    fetchBroilerData();

    if (typeof window !== "undefined") {
      const handleOnline = () => {
        setIsOffline(false);
        fetchBroilerData();
      };

      const handleOffline = () => {
        setIsOffline(true);
        cacheRef.current = readBroilerCache();
        if (isMountedRef.current) {
          setBroilers(cacheRef.current?.data || []);
          if (!cacheRef.current?.data?.length) {
            setLoading(false);
          }
        }
      };

      refreshTimer = window.setInterval(() => {
        fetchBroilerData();
      }, 10 * 60 * 1000);

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      return () => {
        if (refreshTimer) {
          window.clearInterval(refreshTimer);
        }
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }

    return () => {
      if (refreshTimer) {
        window.clearInterval(refreshTimer);
      }
    };
  }, [fetchBroilerData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const healthColorMap = {
    red: "error",
    green: "success",
    amber: "warning",
    orange: "warning",
    yellow: "warning",
    blue: "info",
    light: "light",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box p={4}>
        <Typography variant="h4" gutterBottom mb={5}>
          Production Facilities
        </Typography>
        <Grid container spacing={3}>
          {broilers.map((broiler) => {
            const cardColor = healthColorMap[broiler.health?.toLowerCase()] || "light";
            return (
              <Grid mt={5} item xs={12} sm={6} md={4} key={broiler.id}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    {/* Left Side: Icon */}
                    <MDBox display="flex" flexDirection="column" alignItems="center" mt={-3}>
                      <MDBox
                        component={Link}
                        to={`/dashboard?facility=${broiler.id}`}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        mt={-3}
                        sx={{ textDecoration: "none" }}
                      >
                        <MDBox
                          variant="gradient"
                          bgColor={cardColor}
                          color={cardColor === "light" ? "dark" : "white"}
                          coloredShadow={cardColor}
                          borderRadius="xl"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="4rem"
                          height="4rem"
                        >
                          <MDBox
                            component="img"
                            src={broilerIcon}
                            alt="icon"
                            width="40px"
                            height="40px"
                          />
                        </MDBox>
                      </MDBox>
                    </MDBox>

                    {/* Right Side: Name and Count */}
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        {broiler.name}
                      </MDTypography>
                      <MDTypography variant="h4">{broiler.count}</MDTypography>

                      {/* ? Update link */}
                      <MDTypography
                        component={Link}
                        to={`/production-records?facility=${broiler.id}`}
                        variant="subtitle2"
                        color="secondary"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          display: "block",
                          mt: 2,
                          fontSize: "1.05rem",
                        }}
                      >
                        Update
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Bottom Left Cycle Info */}
                  <MDBox
                    px={3}
                    pt={1}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                  >
                    <MDTypography
                      sx={{ fontSize: "1rem", fontWeight: "bold" }}
                      color="textSecondary"
                    >
                      {broiler.cycle}
                    </MDTypography>
                    <MDTypography
                      sx={{ fontSize: "1rem", fontWeight: "bold" }}
                      color="textSecondary"
                    >
                      Day {broiler.cycle_day}
                    </MDTypography>
                    <MDTypography
                      sx={{ fontSize: "0.9rem", marginBottom: "1px" }}
                      color="textSecondary"
                    >
                      Start: {broiler.start_date}
                    </MDTypography>
                  </MDBox>

                  <Divider sx={{ mt: 0.2 }} />

                  {/* Updated Timestamp */}
                  <MDBox textAlign="left" sx={{ position: "relative", top: "-10px", pl: 3 }}>
                    <MDTypography component="p" variant="button" color="text" display="flex">
                      <MDTypography
                        component="span"
                        variant="button"
                        fontWeight="bold"
                        color="success"
                      >
                        Updated:
                      </MDTypography>
                      &nbsp;{broiler.timestamp}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Snackbar
        open={refreshing}
        message="Refreshing facility data..."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <Snackbar
        open={isOffline}
        message="You're currently in offline mode"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={null}
      />
    </DashboardLayout>
  );
}

export default BroilerLanding;
